/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react'

import { Alert, Dropdown, type DropdownOption } from '@/components/ui'
import { useApi } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import logger from '@/utils/logger'

import type { SeedSelection, SourceUserDetail, SourceUserSummary } from './types'

const log = logger({ category: 'DashboardSeedPanel' })

/** Local profile search — short fragments are fine, but 1 char is just noise. */
const MIN_QUERY = 2

interface DashboardSeedPanelProps {
  /**
   * The company currently picked for the new user; undefined = not picked yet.
   * The company-match warning derives from this live, so changing the company
   * re-checks the already-picked source automatically.
   */
  targetCompanyId?: number
  onChange: (seed: SeedSelection) => void
}

/**
 * Chooses what the new profile's dashboards start as: the standard default
 * dashboard, or a copy of selected dashboards from another Insights user.
 * The source search is not restricted by company; instead every candidate's
 * company is shown, and a live advisory warning appears when the picked source
 * and the new user's company differ.
 */
export const DashboardSeedPanel: React.FC<DashboardSeedPanelProps> = ({ targetCompanyId, onChange }) => {
  const { api } = useApi()
  const { companies } = useCompany()
  const [mode, setMode] = useState<'default' | 'copy'>('default')
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [results, setResults] = useState<SourceUserSummary[] | null>(null)
  const [source, setSource] = useState<SourceUserDetail | null>(null)
  const [loadingSource, setLoadingSource] = useState(false)
  const [selected, setSelected] = useState<DropdownOption[]>([])

  const pickMode = (next: 'default' | 'copy') => {
    setMode(next)
    setQuery('')
    setSearchError('')
    setResults(null)
    setSource(null)
    setSelected([])
    onChange({ mode: next })
  }

  // Autocomplete over the profiles table, debounced; the sequence counter drops
  // responses that come back after the query has moved on.
  const searchSeq = useRef(0)
  useEffect(() => {
    if (mode !== 'copy') return
    const q = query.trim()
    const seq = ++searchSeq.current
    if (q.length < MIN_QUERY) {
      setResults(null)
      setSearchError('')
      setSearching(false)
      return
    }
    setSearching(true)
    const handle = setTimeout(() => {
      api
        .get<SourceUserSummary[]>({
          path: `/app/admin/source-users?q=${encodeURIComponent(q)}`,
          origin: 'DashboardSeedPanel#search',
        })
        .then((response) => {
          if (seq !== searchSeq.current) return
          if (response.error) {
            setResults(null)
            setSearchError(response.message || 'Could not search Insights users')
          } else {
            setSearchError('')
            setResults(response.data ?? [])
          }
          setSearching(false)
        })
        .catch((ex: unknown) => {
          log.error(ex)
          if (seq !== searchSeq.current) return
          setSearchError('Could not search Insights users')
          setSearching(false)
        })
    }, 350)
    return () => clearTimeout(handle)
  }, [mode, query, api])

  const pickSource = async (uid: string) => {
    setLoadingSource(true)
    setResults(null)
    setSource(null)
    setSelected([])
    onChange({ mode: 'copy' })
    try {
      const response = await api.get<SourceUserDetail>({
        path: `/app/admin/source-users/${encodeURIComponent(uid)}`,
        origin: 'DashboardSeedPanel#source',
      })
      if (response.error || !response.data) {
        setSearchError(response.message || 'Could not load that user')
        return
      }
      setSearchError('')
      setSource(response.data)
    } catch (ex) {
      log.error(ex)
      setSearchError('Could not load that user')
    } finally {
      setLoadingSource(false)
    }
  }

  const companyName = (companyId: number | null | undefined): string => {
    if (companyId === null || companyId === undefined) return ''
    return companies.find((c) => Number(c.id) === companyId)?.name ?? `company #${String(companyId)}`
  }

  // Derived live, so changing the new user's company re-checks automatically.
  const matchState: 'match' | 'mismatch' | 'unknown' =
    source === null || source.companyId === null || targetCompanyId === undefined
      ? 'unknown'
      : source.companyId === targetCompanyId
        ? 'match'
        : 'mismatch'

  const companyNote = (detail: SourceUserDetail): string | null => {
    if (detail.companyId === null) {
      return `${detail.email} has no company recorded on their Insights profile yet, so the company match cannot be checked.`
    }
    if (targetCompanyId === undefined) {
      return "Pick the new user's company to check it matches this user's."
    }
    if (matchState === 'mismatch') {
      return 'Companies do not match - dashboards will need to be re-configured'
    }
    return null
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name="seed-mode"
            className="radio radio-sm"
            checked={mode === 'default'}
            onChange={() => pickMode('default')}
          />
          <span>Start with the standard default dashboard</span>
        </label>
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name="seed-mode"
            className="radio radio-sm"
            checked={mode === 'copy'}
            onChange={() => pickMode('copy')}
          />
          <span>Copy dashboards from another user</span>
        </label>
      </div>

      {mode === 'copy' && (
        <div className="ml-7 flex flex-col gap-3">
          <input
            className="input input-bordered input-sm w-full max-w-md"
            placeholder="Type their email or name"
            value={query}
            aria-label="Search Insights users"
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
          />
          {searching && <p className="text-sm text-base-content/70">Searching…</p>}

          {searchError && <Alert type="error" message={searchError} />}

          {results && results.length === 0 && (
            <p className="text-sm text-base-content/70">{`No Insights users found matching '${query.trim()}'.`}</p>
          )}

          {results && results.length > 0 && (
            <ul
              role="listbox"
              aria-label="Insights users"
              className="max-w-md divide-y divide-base-300 rounded-lg border border-base-300 bg-base-100"
            >
              {results.map((user) => (
                <li
                  key={user.uid}
                  role="option"
                  aria-selected={false}
                  onClick={() => void pickSource(user.uid)}
                  className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-base-200"
                >
                  <span className="min-w-0">
                    <span className="block truncate">{user.email}</span>
                    {/* Company up front — the whole point of picking a source is company fit. */}
                    {[user.displayName !== user.email ? user.displayName : '', companyName(user.companyId)].some(
                      Boolean
                    ) && (
                      <span className="block truncate text-xs text-base-content/60">
                        {[user.displayName !== user.email ? user.displayName : '', companyName(user.companyId)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-base-content/60">
                    {user.dashboardCount} dashboard{user.dashboardCount === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {loadingSource && <p className="text-sm text-base-content/70">Loading their dashboards…</p>}

          {source && (
            <p className="text-sm" data-testid="seed-source-summary">
              Copying from <span className="font-medium">{source.email}</span>
              {source.companyId !== null && (
                <span className="text-base-content/60">{` · ${companyName(source.companyId)}`}</span>
              )}
              {matchState === 'match' && (
                <span className="ml-1 text-success" role="img" aria-label="companies match">
                  ✓
                </span>
              )}
              {matchState === 'mismatch' && (
                <span className="ml-1" role="img" aria-label="companies do not match">
                  ⚠️
                </span>
              )}
            </p>
          )}

          {source && companyNote(source) && <Alert type="warning" message={companyNote(source) ?? ''} />}

          {source && source.dashboards.length === 0 && (
            <Alert type="info" message={`${source.email} has no dashboards to copy.`} />
          )}

          {source && source.dashboards.length > 0 && (
            <div className="max-w-md">
              <Dropdown
                label={`Dashboards to copy from ${source.email}`}
                isMultiSelect
                includeSelectAll
                options={source.dashboards.map((d) => ({ value: d.slug, label: d.title }))}
                selectedItem={selected}
                handleMultiSelectItem={(options) => {
                  setSelected(options)
                  onChange({ mode: 'copy', sourceUid: source.uid, dashboardSlugs: options.map((o) => o.value) })
                }}
                placeholder="Pick one or more dashboards"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
